'use client';

import { impliedProbabilityPct } from '@/lib/odds';
import { cn } from '@/lib/cn';

interface Props {
  label: string;
  price: number | null;
  // Whether this cell currently represents the row's primary bet (e.g. the
  // user's preselected outcome). Renders the filled-blue treatment Polymarket
  // uses to highlight the chosen pick.
  filled?: boolean;
  // When true the cell can't be focused — used on `/games` cards where each
  // card is itself a link.
  static?: boolean;
}

/**
 * Inline odds cell — shows a short label plus the implied probability for the
 * outcome's best ask. Polymarket-style: filled blue button when chosen,
 * dim panel otherwise. Static use is fine inside an anchor (no nested
 * button); interactive use needs a separate clickable variant.
 */
export function PriceCell({ label, price, filled, static: isStatic }: Props): JSX.Element {
  const baseClass = cn(
    'flex h-12 min-w-[88px] items-center justify-between gap-1 rounded-md px-3 text-xs font-semibold tabular-nums transition-colors',
    filled
      ? 'bg-accent-strong text-white'
      : 'bg-panel-2 text-text hover:bg-panel-2/80',
    price == null && 'text-muted opacity-70',
  );
  const content = (
    <>
      <span className={cn('text-[11px] font-medium', filled ? 'text-white/90' : 'text-muted')}>
        {label}
      </span>
      <span className="text-sm font-bold">{impliedProbabilityPct(price)}</span>
    </>
  );
  if (isStatic) {
    return <span className={baseClass}>{content}</span>;
  }
  return (
    <button type="button" className={baseClass}>
      {content}
    </button>
  );
}
